import { useTracking } from '@folklore/tracking';
import { useTransition, animated } from '@react-spring/web';
import classNames from 'classnames';
import { differenceInSeconds, parseISO } from 'date-fns';
import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';

import useFailsafeTimerComplete from '../../hooks/useFailsafeTimerComplete';
import { useAnswerQuestion } from '../../hooks/useGame';
import { useEndParticipation } from '../../hooks/useParticipation';
import { getComponentFromName, getImageAtSize } from '../../lib/utils';

import { useLabels } from '../../contexts/LabelsContext';
import { useThemeClassName } from '../../contexts/ThemeContext';
import * as FeedbackComponents from '../animations/feedbacks';
import Button from '../buttons/Button';
import Volume from '../icons/Volume';
import GameTimer from '../partials/GameTimer';
import GameEnd from './GameEnd';

import styles from '../../../styles/games/base.module.scss';

export interface BaseGameProps {
    game: Game;
    participation: Participation | null;
    isValid: boolean | null;
    canPlay: boolean;
    apiRef?: React.RefObject<any> | ((ref: any) => void);
    children: React.ReactNode;
    endChildren?: React.ReactNode;
    banner?: React.ReactNode;
    statusBar?: React.ReactNode;
    reload: () => void;
    withTimer?: boolean;
    withFeedback?: boolean;
    onTimerComplete?: () => void;
    onFeedbackComplete?: () => void;
    onLoadingChange?: (loading: boolean) => void;
    onReset?: () => void;
    className?: string;
    headerClassName?: string;
    timerClassName?: string;
}

export interface GameApi {
    answerQuestion: (questionId: string, newAnswer: any) => void;
    endGame: () => void;
    reset: () => void;
    endTimer: () => void;
    muted: boolean;
}

const BaseGame = ({
    game,
    participation,
    canPlay,
    isValid,
    apiRef,
    children,
    endChildren,
    banner,
    statusBar = null,
    reload = () => {},
    withTimer = false,
    withFeedback = false,
    onLoadingChange = null,
    onTimerComplete: onProvidedTimerComplete = null,
    onFeedbackComplete: onProvidedFeedbackComplete = null,
    onReset = null,
    className = null,
    headerClassName = null,
    timerClassName = null,
}: BaseGameProps) => {
    const themeClassName = useThemeClassName(styles);
    const tracking = useTracking();
    const { gameFeedbackValid, gameFeedbackInvalid } = useLabels();

    const {
        // id: gameId,
        type,
        title,
        subtitle,
        description,
        // image = null,
        headerImage = null,
        theme: gameTheme = null,
        blocks = [],
    } = game || {};

    const questions = 'questions' in game ? game.questions || null : null;
    const gameDuration = 'duration' in game ? game.duration || 30 : 30;

    const endBlocks = useMemo(
        () => (blocks || []).filter(({ placement }) => placement === 'end'),
        [blocks],
    );

    const {
        feedbackComponent = null,
        name: gameThemeName = null,
        fullscreen = false,
        withFixedHeader = false,
        withHeaderTitle = true,
        withBackgroundMusic = false,
    } = gameTheme || {};

    const FeedbackComponent = withFeedback
        ? getComponentFromName(FeedbackComponents, feedbackComponent, null) || null
        : null;

    const mediumHeaderImage = getImageAtSize(headerImage, 'medium');
    const { url: mediumHeaderImageUrl = null } = mediumHeaderImage || {};
    const { id: participationId, endedAt: participationEndedAt = null } = participation || {};

    const endedDiff =
        participationEndedAt !== null
            ? differenceInSeconds(parseISO(participationEndedAt), new Date())
            : null;

    const duration = useMemo(
        () => Math.max(0, endedDiff !== null ? endedDiff : gameDuration),
        [participationId, participationEndedAt, gameDuration],
    );

    const participationEnded = endedDiff !== null && endedDiff <= 0;

    // console.log('participationEnded', participationEnded);

    const [ended, setEnded] = useState(participationEnded);
    const [timerComplete, setTimerComplete] = useState(participationEnded);
    const [feedbackValidity, setFeedback] = useState<boolean | null>(null);
    const [feedbackQuestion, setFeedbackQuestion] = useState<object | null>(null);

    const reset = useCallback(() => {
        setEnded(false);
        setTimerComplete(false);
        setFeedback(null);
        // setFeedback(null);
        if (onReset !== null) {
            onReset();
        }
        // console.log('reset yo', participationId);
    }, [setEnded, setTimerComplete, setFeedback, onReset, participationId]);

    useEffect(() => {
        setEnded(participationEnded);
        setTimerComplete(participationEnded);
    }, [participationId]);

    const timerStarted = !timerComplete && canPlay && !ended;

    const { answerQuestionAsync } = useAnswerQuestion({
        game,
        participation,
    });

    const answerQuestion = useCallback(
        (questionId, newAnswer) => {
            if (onLoadingChange !== null) {
                onLoadingChange(true);
            }
            answerQuestionAsync({
                questionId,
                answer: newAnswer,
            }).then(({ participation: updatedParticipation, participant: updatedParticipant }) => {
                if (onLoadingChange !== null) {
                    onLoadingChange(false);
                }
                const {
                    // id: updatedParticipationId = null,
                    answers = [],
                    ended: newEnded = false,
                } = updatedParticipation || {};
                const question = (questions || []).find(({ id = null }) => id === questionId);
                const answer = (answers || []).find(({ question: q = null }) => q === questionId);

                const { valid = null, validated = false, value } = answer || {};
                if (validated && valid !== null && FeedbackComponent !== null) {
                    setFeedback(valid);
                    setFeedbackQuestion(question);
                }

                if (FeedbackComponent === null && newEnded) {
                    setEnded(newEnded);
                }

                setTimerComplete(newEnded);

                tracking.trackInteraction('game_answer', {
                    game,
                    participant: updatedParticipant,
                    participation: updatedParticipation,
                    answer: value,
                    valid,
                    validated,
                });

                if (newEnded) {
                    tracking.trackInteraction('game_ended', {
                        game,
                        participant: updatedParticipant,
                        participation: updatedParticipation,
                    });
                }
            });
        },
        [
            game,
            participation,
            answerQuestionAsync,
            tracking,
            participationId,
            setTimerComplete,
            onLoadingChange,
            FeedbackComponent,
            questions,
            setFeedbackQuestion,
        ],
    );

    const { endParticipationAsync } = useEndParticipation(participation);

    const endGame = useCallback(() => {
        if (onLoadingChange !== null) {
            onLoadingChange(true);
        }
        endParticipationAsync().then(
            ({ participation: updatedParticipation, participant: updatedParticipant }) => {
                if (onLoadingChange !== null) {
                    onLoadingChange(false);
                }
                const { ended: newEnded = false } = updatedParticipation || {};
                setTimerComplete(newEnded);

                if (newEnded) {
                    tracking.trackInteraction('game_ended', {
                        game,
                        participant: updatedParticipant,
                        participation: updatedParticipation,
                    });
                }
            },
        );
    }, [endParticipationAsync, tracking, participationId, setTimerComplete]);

    const onCustomTimerComplete = useCallback(() => {
        setTimerComplete(true);
        if (feedbackValidity === null && !participationEnded) {
            setFeedback(false);
        }

        if (!participationEnded) {
            console.log('onCustomTimerComplete', endGame);
            endGame();
        }

        if (onProvidedTimerComplete !== null) {
            onProvidedTimerComplete();
        }
    }, [
        participationId,
        participationEndedAt,
        participationEnded,
        answerQuestion,
        endGame,
        feedbackValidity,
    ]);

    const onTimerComplete = useFailsafeTimerComplete(
        onCustomTimerComplete,
        participationEndedAt,
        !withTimer,
    );

    const onFeedbackComplete = useCallback(() => {
        setFeedback(null);
        setEnded(
            participationEndedAt !== null &&
                differenceInSeconds(parseISO(participationEndedAt), new Date()) <= 0,
        );
        if (onProvidedFeedbackComplete !== null) {
            onProvidedFeedbackComplete();
        }
    }, [participationEndedAt, onProvidedFeedbackComplete]);

    const transitions = useTransition(ended ? 'end' : 'game', {
        initial: { x: '0%' },
        from: { x: '100%' },
        enter: { x: '0%' },
        leave: { x: '-100%', position: 'absolute', top: 0, left: 0, width: '100%' },
        keys: (key) => key,
        // config: { duration: 30000 },
    });

    const { audio = null } = game || {};
    const { url: audioUrl = null } = audio || {};
    const hasAudio = withBackgroundMusic && audioUrl !== null;

    const started = !timerComplete && canPlay && audioUrl !== null;

    const [muted, setMuted] = useState(!started);
    const onClickMuted = useCallback(() => {
        setMuted(!muted);
    }, [muted, setMuted]);

    useEffect(() => {
        // Auto-Mute on ended
        if (ended) {
            setMuted(true);
        }
    }, [ended, setMuted]);

    const audioRef = useRef<HTMLAudioElement | null>(null);
    useEffect(() => {
        if (hasAudio) {
            audioRef.current = document.createElement('audio');
            audioRef.current.src = audioUrl;
            audioRef.current.load();
            audioRef.current.playbackRate = 1.0;
            try {
                if (!muted) {
                    audioRef.current.play();
                } else {
                    audioRef.current.pause();
                }
            } catch (e) {
                console.log(e);
            }
        }

        return () => {
            if (audioRef.current !== null) {
                audioRef.current?.pause();
            }
        };
    }, [audioUrl, muted]);

    useImperativeHandle(
        apiRef,
        (): GameApi => ({
            answerQuestion,
            endGame,
            reset,
            endTimer: () => setTimerComplete(true),
            muted,
        }),
        [answerQuestion, endGame, reset, setTimerComplete, muted],
    );

    return (
        <div
            className={classNames([
                styles.container,
                `game-${type}`,
                themeClassName,
                {
                    [styles.fullscreen]: fullscreen,
                    [styles.ended]: ended === true,
                },
                className,
            ])}
            data-game-theme={gameThemeName}
        >
            {banner}
            <div
                className={classNames([
                    styles.header,
                    'game-header',
                    headerClassName,
                    { [styles.fixed]: withFixedHeader },
                ])}
            >
                {mediumHeaderImageUrl !== null ? (
                    <img src={mediumHeaderImageUrl} alt={title} className={styles.image} />
                ) : null}
                {withHeaderTitle && mediumHeaderImageUrl === null ? (
                    <h1 className={styles.title}>{title}</h1>
                ) : null}
                {subtitle !== null ? <h2 className={styles.subtitle}>{subtitle}</h2> : null}
                {description !== null && !ended ? (
                    <div className={styles.description}>{description}</div>
                ) : null}
                {(hasAudio || withTimer || statusBar !== null) && !participationEnded ? (
                    <div className={classNames([styles.gameStatusBar, 'game-status-bar'])}>
                        {statusBar}
                        {hasAudio ? (
                            <Button
                                className={classNames([styles.volume, 'game-volume'])}
                                onClick={onClickMuted}
                            >
                                <Volume className={styles.mute} muted={muted} />
                            </Button>
                        ) : null}
                        {withTimer ? (
                            <GameTimer
                                duration={duration}
                                started={timerStarted}
                                onComplete={onTimerComplete}
                                className={classNames([styles.timer, timerClassName, 'game-timer'])}
                            />
                        ) : null}
                    </div>
                ) : null}
            </div>
            <div className={classNames([styles.gameContainer, 'game-container'])}>
                {transitions((style, item) => {
                    if (item === 'end') {
                        return (
                            <animated.div style={style} className={styles.end}>
                                <GameEnd
                                    game={game}
                                    participation={participation}
                                    isValid={isValid}
                                    blocks={endBlocks}
                                    reload={reload}
                                    onReplay={reset}
                                >
                                    {endChildren}
                                </GameEnd>
                            </animated.div>
                        );
                    }
                    return (
                        <animated.div style={style} className={styles.game}>
                            {children}
                        </animated.div>
                    );
                })}
            </div>
            {FeedbackComponent !== null ? (
                <FeedbackComponent
                    className={classNames([
                        styles.feedback,
                        {
                            [styles.isActive]: feedbackValidity !== null,
                        },
                    ])}
                    valid={feedbackValidity}
                    validLabel={gameFeedbackValid}
                    invalidLabel={gameFeedbackInvalid}
                    question={feedbackQuestion}
                    onAnimationComplete={onFeedbackComplete}
                />
            ) : null}
        </div>
    );
};

export default BaseGame;
